import { fontSize as egFontSize } from '../../../../design-system/editorial-grid/tokens'
import React, {
  useEffect,
  useRef,
  useId,
  useCallback,
} from 'react'
import { createPortal } from 'react-dom'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  /** Controls the max-width of the dialog panel. Defaults to 'md'. */
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses: Record<string, string> = {
  sm: 'modal-sm max-w-sm',
  md: 'modal-md max-w-md',
  lg: 'modal-lg max-w-lg',
}

/** All standard focusable element selectors */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
}

/**
 * Accessible modal dialog primitive.
 *
 * Features:
 * - Renders via createPortal to document.body to escape stacking contexts
 * - focus trap: Tab / Shift+Tab cycles within the modal
 * - Escape key closes the modal
 * - Overlay click closes the modal; click inside dialog does not
 * - On open: moves focus into the modal
 * - On close: restores focus to the element that was active before opening
 * - role="dialog", aria-modal="true", aria-labelledby on title
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: ModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  // Capture the element that had focus before the modal was opened so we can
  // restore it when the modal closes. Using time.Now() equivalent avoided via
  // ref so re-renders don't clobber the saved value.
  const previousFocusRef = useRef<Element | null>(null)

  // Save the currently active element when opening; restore when closing.
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement
      // Move focus into the modal on the next tick so the DOM is painted.
      const raf = requestAnimationFrame(() => {
        if (!dialogRef.current) return
        const focusables = getFocusableElements(dialogRef.current)
        if (focusables.length > 0) {
          focusables[0].focus()
        } else {
          // Fall back to the container itself so keyboard events are captured.
          dialogRef.current.focus()
        }
      })
      return () => cancelAnimationFrame(raf)
    } else {
      // Restore focus when the modal closes.
      const prev = previousFocusRef.current
      if (prev && prev instanceof HTMLElement) {
        prev.focus()
      }
      previousFocusRef.current = null
    }
  }, [isOpen])

  // Escape key + focus trap handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = getFocusableElements(dialogRef.current)
        if (focusables.length === 0) return

        const first = focusables[0]
        const last = focusables[focusables.length - 1]

        if (e.shiftKey) {
          // Shift+Tab from first → wrap to last
          if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
          }
        } else {
          // Tab from last → wrap to first
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  // Guard for environments where document is not available (SSR safety).
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      data-modal-overlay=""
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={sizeClasses[size]}
        style={{
          background: 'var(--surface)',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          padding: 24,
          width: '100%',
          margin: '0 16px',
          maxHeight: '90vh',
          overflowY: 'auto',
          outline: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <h3
            id={titleId}
            style={{ fontSize: egFontSize.lg, fontWeight: 700, margin: 0 }}
          >
            {title}
          </h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.5rem',
              lineHeight: 1,
              padding: '4px 8px',
              color: 'var(--text-secondary)',
            }}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        {children}
      </div>
    </div>,
    document.body
  )
}
